import agent, { type LaunchableFromList } from "server";
import { api, type AxiosRes, type BaseRes } from "server/config";
import type { LifeCycleScriptAttr } from "components/Environment/shared/BuildTypes";

export type LaunchableVisibility = "public" | "authenticated";

export class LaunchableRequestError extends Error {
  readonly notFound: boolean;
  readonly accessDenied: boolean;

  constructor(status?: number) {
    super("Error fetching launchable data");
    this.name = "LaunchableRequestError";
    this.notFound = status === 404;
    this.accessDenied = status === 403;
  }
}

export async function getLaunchable(
  launchableId: string,
  visibility: LaunchableVisibility,
): Promise<LaunchableFromList> {
  const response =
    visibility === "public"
      ? await agent.Launchables.getPublicLaunchable(launchableId)
      : await agent.Launchables.getLaunchableWithAuth(launchableId);

  if (!response.success || !response.data) {
    throw new LaunchableRequestError(response.status);
  }

  return response.data;
}

type LaunchableLifecycleScriptResponse = {
  data?: {
    lifecycle_script: string;
    attrs: LifeCycleScriptAttr;
  };
} & AxiosRes &
  BaseRes;

export async function getLaunchableLifecycleScript(
  launchableId: string,
  lifecycleScriptId: string,
  signal?: AbortSignal,
): Promise<string> {
  const response = (await api.get("/api/launchable/lifecycle-script", {
    params: {
      envId: launchableId,
      scriptId: lifecycleScriptId,
    },
    signal,
  })) as unknown as LaunchableLifecycleScriptResponse;

  if (!response.success) {
    throw new Error(response.message || "Error fetching lifecycle script");
  }

  return response.data?.attrs?.script ?? "";
}
